import { InputSummary, InputSummaryItem } from '../../../input-summary';
import InputSummaryUtils from '../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../data-structures/unit-value.model';
import { Loan } from './loan.model';
import { Partnership } from './partnership.model';
import Utils from '../../../../util';

export interface FinancialInvestmentInterface {
  discountRate: UnitValue;
  incomeTax: UnitValue;
  residualValue: UnitValue;
  distributions: UnitValue;
  loans: Loan[];
  partnerships: Partnership[];
  contributionsUseCurrency: boolean;
  discountToggle: boolean;
  incomeToggle: boolean;
  valueToggle: boolean;
  dividendsToggle: boolean;
}

export class FinancialInvestment {
  public discountRate: UnitValue;
  public incomeTax: UnitValue;
  public residualValue: UnitValue;
  public distributions: UnitValue;
  public loans: Loan[];
  public partnerships: Partnership[];
  public contributionsUseCurrency: boolean;
  public discountToggle: boolean;
  public incomeToggle: boolean;
  public valueToggle: boolean;
  public dividendsToggle: boolean;

  constructor(param: FinancialInvestmentInterface = {
    discountRate: new UnitValue({ value: 0, unit: '000000000000000000000401' }),
    incomeTax: new UnitValue({ value: 0, unit: '000000000000000000000401' }),
    residualValue: new UnitValue({ value: 100, unit: '000000000000000000005401' }),
    distributions: new UnitValue({ value: 0, unit: '000000000000000000005501' }),
    loans: [],
    partnerships: [],
    contributionsUseCurrency: false,
    discountToggle: false,
    incomeToggle: false,
    valueToggle: false,
    dividendsToggle: false
  }) {
    this.discountRate = new UnitValue(param.discountRate);
    this.incomeTax = new UnitValue(param.incomeTax);
    this.residualValue = new UnitValue(param.residualValue);
    this.distributions = new UnitValue(param.distributions);
    this.loans = [];
    for (const data of param.loans) {
      this.loans.push(new Loan(data));
    }
    this.partnerships = [];
    for (const data of param.partnerships) {
      this.partnerships.push(new Partnership(data));
    }
    if (Utils.isBoolean(param.contributionsUseCurrency)) {
      this.contributionsUseCurrency = param.contributionsUseCurrency;
    } else {
      this.contributionsUseCurrency = false;
    }
    if (Utils.isBoolean(param.discountToggle)) {
      this.discountToggle = param.discountToggle;
    } else {
      this.discountToggle = false;
    }
    if (Utils.isBoolean(param.incomeToggle)) {
      this.incomeToggle = param.incomeToggle;
    } else {
      this.incomeToggle = false;
    }
    if (Utils.isBoolean(param.valueToggle)) {
      this.valueToggle = param.valueToggle;
    } else {
      this.valueToggle = false;
    }
    if (Utils.isBoolean(param.dividendsToggle)) {
      this.dividendsToggle = param.dividendsToggle;
    } else {
      this.dividendsToggle = false;
    }
  }

  static deepCopy(f: FinancialInvestment) {
    let copy;
    if (f) {
      copy = new FinancialInvestment(f);
      return copy;
    }
    throw new Error('financial investment copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);

    const loansInputSummary = new InputSummary();
    for (const loan of this.loans) {
      const index = this.loans.indexOf(loan);
      loansInputSummary.subSection[loan.name] = loan.getInputSummary([...path, 'loans', String(index)]);
    }
    inputSummary.subSection[FinancialInvestmentPropName.loans] = loansInputSummary;

    const partnershipInputSummary = new InputSummary();
    for (const partnership of this.partnerships) {
      const index = this.partnerships.indexOf(partnership);
      partnershipInputSummary.subSection[partnership.name] = partnership.getInputSummary([...path, 'partnerships', String(index)]);
    }
    inputSummary.subSection[FinancialInvestmentPropName.partnerships] = partnershipInputSummary;

    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FinancialInvestmentPropName.discountRate, this.discountRate, [...path, 'discountRate']);
    inputs = InputSummaryUtils.performPush(inputs, FinancialInvestmentPropName.incomeTax, this.incomeTax, [...path, 'incomeTax']);
    inputs = InputSummaryUtils.performPush(inputs, FinancialInvestmentPropName.residualValue, this.residualValue, [...path, 'residualValue']);
    inputs = InputSummaryUtils.performPush(inputs, FinancialInvestmentPropName.distributions, this.distributions, [...path, 'distributions']);
    return inputs;
  }

  /**
   * Get the total contribution percentage, if percentage units are selected.
   */
  getTotalContributionPercentage(): number {
    if (this.contributionsUseCurrency) {
      return null;
    }
    let total = 0;
    if (this.partnerships && this.partnerships.length > 0) {
      for (const item of this.partnerships) {
        total += item.investmentContribution.value;
      }
      return total;
    }
  }

  /**
   * Get the total share percentage.
   */
  getTotalSharePercentage(): number {
    let total = 0;
    if (this.partnerships && this.partnerships.length > 0) {
      for (const item of this.partnerships) {
        total += item.netIncomeShare.value;
      }
    }
    return total;
  }
}

export enum FinancialInvestmentPropName {
  discountRate = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.DISCOUNT_RATE',
  incomeTax = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.INCOME_TAX',
  residualValue = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.RESIDUAL_VALUE',
  distributions = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.DISTRIBUTIONS',
  loans = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.LOANS',
  partnerships = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.PARTNERSHIPS',
  id_discountRate = 'discount rate',
  id_incomeTax = 'income tax',
  id_residualValue = 'plant residual value',
  id_distributions = 'dividends and other distributions',
  id_loans = 'loan financing',
  id_partnerships = 'partnership'
}
